import { SWRConfig } from 'swr';

import { toast } from '@apps/web/lib/app_notifications';
import { ApiError } from '@libraries/typescript/api_client';

const ERROR_TOAST_THROTTLE_MS = 60_000;
let lastErrorToastTimestamp = 0;

export function shouldShowSWRToastForError(error: unknown): boolean {
    if (!(error instanceof ApiError)) {
        return true;
    }

    if (error.isNetworkError) {
        return false;
    }

    // Handled by the auth flow
    if (error.status === 401 || error.status === 403) {
        return false;
    }

    // This is primarily happening when switching between sandbox/live mode.
    // There's probably a cleaner way to handle this with a distinct status
    // code for environment mismatches, but keep this behavior for now.
    // The WellSky rate selector also relies on this to render its inline
    // "shift may have been removed" placeholder for the typed
    // `wellsky_rate_info_not_found` 404 instead of a global red toast.
    if (error.status === 404) {
        return false;
    }

    // Recovery is automatic once the client cooldown expires. A red error
    // toast every minute encourages users to refresh, recreating the request
    // herd this rate limiter is meant to damp.
    if (error.status === 429) {
        return false;
    }

    return true;
}

export function SWRProvider({ children }: { children: React.ReactNode }) {
    return (
        <SWRConfig
            value={{
                onError: (error, key) => {
                    console.error('SWR Error:', error, 'Key:', key);

                    if (!shouldShowSWRToastForError(error)) {
                        return;
                    }

                    const now = Date.now();
                    if (
                        now - lastErrorToastTimestamp >=
                        ERROR_TOAST_THROTTLE_MS
                    ) {
                        lastErrorToastTimestamp = now;
                        let description = 'An unknown API error occurred.';

                        if (error instanceof ApiError) {
                            description = error.message;
                        } else if (error instanceof Error) {
                            description = error.message;
                        }

                        toast.error('Error', {
                            persistNotification: false,
                            description,
                            duration: 8000,
                        });
                    }
                },
                shouldRetryOnError: false,
            }}
        >
            {children}
        </SWRConfig>
    );
}
